import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';

import { IconBillCheck } from '../../components/layout/icons';
import { CkEditorField } from '../../components/ui/CkEditorField';
import { ApiError } from '../../lib/api-client';
import { confirmUpdate } from '../../lib/confirm-service';
import { doctorDisplayName } from '../../lib/doctor-name';
import { useToast } from '../../lib/toast';
import { toDateTimeInput } from '../ipd-daycare/ipd-daycare.utils';
import { useOpdPrescription, useSaveOpdPrescription } from './opd.queries';
import { dateTimeInput, todayInput } from './opd.utils';
import type { OpdRegistration, UpsertOpdPrescriptionPayload } from './types';

interface VitalsRow { height: string; weight: string; pulse: string; bpSystolic: string; bpDiastolic: string; temperature: string }
interface ComplaintRow { date: string; complaint: string; frequency: string; severity: string; duration: string }
interface DiagnosisRow { date: string; diagnosis: string; duration: string }
interface MedicineRow { composition: string; medicine: string; dose: string; when: string; frequency: string; duration: string; notes: string }

const blankVitals = (): VitalsRow => ({ height: '', weight: '', pulse: '', bpSystolic: '', bpDiastolic: '', temperature: '' });
const blankComplaint = (): ComplaintRow => ({ date: todayInput(), complaint: '', frequency: '', severity: '', duration: '' });
const blankDiagnosis = (): DiagnosisRow => ({ date: todayInput(), diagnosis: '', duration: '' });
const blankMedicine = (): MedicineRow => ({ composition: '', medicine: '', dose: '', when: '', frequency: '', duration: '', notes: '' });

const FREQUENCY_OPTIONS = ['Once a day', 'Twice a day', 'Thrice a day', 'Four times a day'];
const SEVERITY_OPTIONS = ['Mild', 'Moderate', 'Severe'];
const DURATION_OPTIONS = ['1 Day', '3 Days', '5 Days', '7 Days', '10 Days', '15 Days', '1 Month'];
const DIAGNOSIS_OPTIONS = ['Hypertension', 'Type 2 Diabetes Mellitus', 'Common Cold', 'Viral Fever', 'Migraine', 'Gastritis', 'Upper Respiratory Tract Infection'];
const DOSE_OPTIONS = ['1 Tablet', '2 Tablets', '1/2 Tablet', '5 ml', '10 ml'];
const WHEN_OPTIONS = ['Before Food', 'After Food', 'Empty Stomach', 'With Food'];

const Select = ({ value, options, onChange }: { value: string; options: string[]; onChange: (value: string) => void }) => (
  <select value={value} onChange={(event) => onChange(event.target.value)}>
    <option value="">Select</option>
    {options.map((option) => <option key={option} value={option}>{option}</option>)}
  </select>
);

const text = (value: string | null) => value ?? '';
const dateText = (value: string | null) => value ? value.slice(0, 10) : todayInput();
const optionalDate = (value: string) => value.trim() ? value : null;

export const OpdPrescriptionForm = ({ row, editMode = false }: { row: OpdRegistration; editMode?: boolean }) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const query = useOpdPrescription(row.id, editMode);
  const saveMutation = useSaveOpdPrescription(row.id, editMode ? 'edit' : 'create');
  const [prescriptionDate, setPrescriptionDate] = useState(dateTimeInput);
  const [vitals, setVitals] = useState<VitalsRow[]>([blankVitals()]);
  const [complaints, setComplaints] = useState<ComplaintRow[]>([blankComplaint()]);
  const [diagnoses, setDiagnoses] = useState<DiagnosisRow[]>([blankDiagnosis()]);
  const [medicines, setMedicines] = useState<MedicineRow[]>([blankMedicine()]);
  const [testsNeeded, setTestsNeeded] = useState('');
  const [advice, setAdvice] = useState('');

  const clearForm = useCallback(() => {
    setPrescriptionDate(dateTimeInput());
    setVitals([blankVitals()]);
    setComplaints([blankComplaint()]);
    setDiagnoses([blankDiagnosis()]);
    setMedicines([blankMedicine()]);
    setTestsNeeded('');
    setAdvice('');
  }, []);

  useEffect(() => {
    if (!editMode) clearForm();
  }, [clearForm, editMode, row.id]);

  useEffect(() => {
    if (!editMode || !query.data) return;
    setPrescriptionDate(toDateTimeInput(query.data.prescriptionDate));
    setVitals(query.data.vitals.length > 0
      ? query.data.vitals.map((item) => ({ height: text(item.height), weight: text(item.weight), pulse: text(item.pulse), bpSystolic: text(item.bpSystolic), bpDiastolic: text(item.bpDiastolic), temperature: text(item.temperature) }))
      : [blankVitals()]);
    setComplaints(query.data.complaints.length > 0
      ? query.data.complaints.map((item) => ({ date: dateText(item.date), complaint: text(item.complaint), frequency: text(item.frequency), severity: text(item.severity), duration: text(item.duration) }))
      : [blankComplaint()]);
    setDiagnoses(query.data.diagnoses.length > 0
      ? query.data.diagnoses.map((item) => ({ date: dateText(item.date), diagnosis: text(item.diagnosis), duration: text(item.duration) }))
      : [blankDiagnosis()]);
    setMedicines(query.data.medicines.length > 0
      ? query.data.medicines.map((item) => ({ composition: text(item.composition), medicine: text(item.medicine), dose: text(item.dose), when: text(item.when), frequency: text(item.frequency), duration: text(item.duration), notes: text(item.notes) }))
      : [blankMedicine()]);
    setTestsNeeded(text(query.data.testsNeeded));
    setAdvice(text(query.data.advice));
  }, [editMode, query.data]);

  const updateRow = <T,>(setRows: Dispatch<SetStateAction<T[]>>, index: number, patch: Partial<T>) =>
    setRows((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item));
  const removeRow = <T,>(setRows: Dispatch<SetStateAction<T[]>>, index: number) =>
    setRows((current) => current.length > 1 ? current.filter((_, itemIndex) => itemIndex !== index) : current);
  const rowHasValue = <T extends object>(row: T) => Object.values(row).some((value) => typeof value === 'string' && value.trim().length > 0);

  const submit = () => {
    if (editMode && !query.data) {
      showToast('The prescription could not be found for editing.', 'error');
      return;
    }
    if (!prescriptionDate) {
      showToast('Date is required.', 'error');
      return;
    }
    const payload: UpsertOpdPrescriptionPayload = {
      prescriptionDate,
      vitals: vitals.map((item) => ({ ...item })),
      complaints: complaints.map((item) => ({ ...item, date: optionalDate(item.date) })),
      diagnoses: diagnoses.map((item) => ({ ...item, date: optionalDate(item.date) })),
      medicines: medicines.map((item) => ({ ...item })),
      testsNeeded: testsNeeded || null,
      advice: advice || null,
    };
    const isUpdate = editMode;
    const run = () => saveMutation.mutate(payload, {
      onSuccess: () => {
        showToast(isUpdate ? 'Prescription updated successfully.' : 'Prescription created successfully.', 'success');
        if (!isUpdate) {
          clearForm();
          void navigate('/opd/prescriptions');
        }
      },
      onError: (error) => showToast(error instanceof ApiError ? error.message : 'The prescription could not be saved.', 'error'),
    });
    if (!editMode) {
      run();
      return;
    }
    void confirmUpdate('this OPD prescription').then((confirmed) => {
      if (confirmed) run();
    });
  };

  return (
    <div className="opd-prescription">
      <header className="opd-bill-banner">
        <div className="opd-bill-banner-row"><span>{editMode ? 'Update Prescription' : 'Add Prescription'} - {row.patientSnapshot.name}</span></div>
        <p>OPD No {row.opdNumber}{editMode ? ' - Saved' : ' - Draft'}</p>
      </header>

      <div className="opd-prescription-top">
        <span className="opd-prescription-tag"><IconBillCheck />OPD</span>
        <label><span className="opd-label-text">Date<span className="opd-required">*</span></span><input type="datetime-local" value={prescriptionDate} onChange={(event) => setPrescriptionDate(event.target.value)} required /></label>
        <label><span className="opd-label-text">Patient Name</span><input value={`${row.patientSnapshot.name}${row.patientSnapshot.uhid ? ` (${row.patientSnapshot.uhid})` : ''}`} readOnly /></label>
        <label><span className="opd-label-text">Doctor&apos;s Name</span><input value={`${doctorDisplayName(row.doctor.name)} (${row.department.name})`} readOnly /></label>
      </div>

      <section className="opd-section opd-vitals-section"><h2>Vitals</h2>
        <div className="table-scroll">
          <table className="opd-charge-table opd-prescription-table">
            <thead><tr><th>Height(cm)</th><th>Weight(kg)</th><th>Pulse(bpm)</th><th>BP(mmHg)</th><th>Temperature(F)</th></tr></thead>
            <tbody>
              {vitals.map((row_, index) => (
                <tr key={index}>
                  <td><input value={row_.height} onChange={(event) => updateRow(setVitals, index, { height: event.target.value })} /></td>
                  <td><input value={row_.weight} onChange={(event) => updateRow(setVitals, index, { weight: event.target.value })} /></td>
                  <td><input value={row_.pulse} onChange={(event) => updateRow(setVitals, index, { pulse: event.target.value })} /></td>
                  <td className="opd-prescription-bp"><input value={row_.bpSystolic} onChange={(event) => updateRow(setVitals, index, { bpSystolic: event.target.value })} /><span>/</span><input value={row_.bpDiastolic} onChange={(event) => updateRow(setVitals, index, { bpDiastolic: event.target.value })} /></td>
                  <td><input value={row_.temperature} onChange={(event) => updateRow(setVitals, index, { temperature: event.target.value })} /></td>
                  <td className="opd-charge-narrow">
                    {index === vitals.length - 1
                      ? <button className="opd-add" type="button" onClick={() => setVitals((current) => [...current, blankVitals()])} disabled={!rowHasValue(row_)} title={rowHasValue(row_) ? undefined : 'Fill in at least one field before adding another row'}>+</button>
                      : <button type="button" onClick={() => removeRow(setVitals, index)} aria-label="Remove vitals row">×</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="opd-section"><h2>Complaints</h2>
        <div className="table-scroll">
          <table className="opd-charge-table opd-prescription-table">
            <thead><tr><th>Date</th><th>Complaints</th><th>Frequency</th><th>Severity</th><th>Duration</th><th className="opd-charge-narrow" /></tr></thead>
            <tbody>
              {complaints.map((row_, index) => (
                <tr key={index}>
                  <td><input type="date" value={row_.date} onChange={(event) => updateRow(setComplaints, index, { date: event.target.value })} /></td>
                  <td><input value={row_.complaint} onChange={(event) => updateRow(setComplaints, index, { complaint: event.target.value })} /></td>
                  <td><Select value={row_.frequency} options={FREQUENCY_OPTIONS} onChange={(value) => updateRow(setComplaints, index, { frequency: value })} /></td>
                  <td><Select value={row_.severity} options={SEVERITY_OPTIONS} onChange={(value) => updateRow(setComplaints, index, { severity: value })} /></td>
                  <td><Select value={row_.duration} options={DURATION_OPTIONS} onChange={(value) => updateRow(setComplaints, index, { duration: value })} /></td>
                  <td className="opd-charge-narrow">
                    {index === complaints.length - 1
                      ? <button className="opd-add" type="button" onClick={() => setComplaints((current) => [...current, blankComplaint()])} disabled={!rowHasValue(row_)} title={rowHasValue(row_) ? undefined : 'Fill in at least one field before adding another row'}>+</button>
                      : <button type="button" onClick={() => removeRow(setComplaints, index)} aria-label="Remove complaint row">×</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="opd-section"><h2>Diagnosis</h2>
        <div className="table-scroll">
          <table className="opd-charge-table opd-prescription-table">
            <thead><tr><th>Date</th><th>Diagnosis</th><th>Duration</th><th className="opd-charge-narrow" /></tr></thead>
            <tbody>
              {diagnoses.map((row_, index) => (
                <tr key={index}>
                  <td><input type="date" value={row_.date} onChange={(event) => updateRow(setDiagnoses, index, { date: event.target.value })} /></td>
                  <td><Select value={row_.diagnosis} options={DIAGNOSIS_OPTIONS} onChange={(value) => updateRow(setDiagnoses, index, { diagnosis: value })} /></td>
                  <td><Select value={row_.duration} options={DURATION_OPTIONS} onChange={(value) => updateRow(setDiagnoses, index, { duration: value })} /></td>
                  <td className="opd-charge-narrow">
                    {index === diagnoses.length - 1
                      ? <button className="opd-add" type="button" onClick={() => setDiagnoses((current) => [...current, blankDiagnosis()])} disabled={!rowHasValue(row_)} title={rowHasValue(row_) ? undefined : 'Fill in at least one field before adding another row'}>+</button>
                      : <button type="button" onClick={() => removeRow(setDiagnoses, index)} aria-label="Remove diagnosis row">×</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="opd-section"><h2>Medicines</h2>
        <div className="table-scroll">
          <table className="opd-charge-table opd-prescription-table">
            <thead><tr><th>Composition</th><th>Medicine</th><th>Dose</th><th>When</th><th>Frequency</th><th>Duration</th><th>Notes/Instructions</th><th className="opd-charge-narrow" /></tr></thead>
            <tbody>
              {medicines.map((row_, index) => (
                <tr key={index}>
                  <td><input value={row_.composition} onChange={(event) => updateRow(setMedicines, index, { composition: event.target.value })} /></td>
                  <td><input value={row_.medicine} onChange={(event) => updateRow(setMedicines, index, { medicine: event.target.value })} /></td>
                  <td><Select value={row_.dose} options={DOSE_OPTIONS} onChange={(value) => updateRow(setMedicines, index, { dose: value })} /></td>
                  <td><Select value={row_.when} options={WHEN_OPTIONS} onChange={(value) => updateRow(setMedicines, index, { when: value })} /></td>
                  <td><Select value={row_.frequency} options={FREQUENCY_OPTIONS} onChange={(value) => updateRow(setMedicines, index, { frequency: value })} /></td>
                  <td><Select value={row_.duration} options={DURATION_OPTIONS} onChange={(value) => updateRow(setMedicines, index, { duration: value })} /></td>
                  <td><input value={row_.notes} onChange={(event) => updateRow(setMedicines, index, { notes: event.target.value })} /></td>
                  <td className="opd-charge-narrow">
                    {index === medicines.length - 1
                      ? <button className="opd-add" type="button" onClick={() => setMedicines((current) => [...current, blankMedicine()])} disabled={!rowHasValue(row_)} title={rowHasValue(row_) ? undefined : 'Fill in at least one field before adding another row'}>+</button>
                      : <button type="button" onClick={() => removeRow(setMedicines, index)} aria-label="Remove medicine row">×</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="opd-section"><h2>Tests Needed</h2>
        <input value={testsNeeded} onChange={(event) => setTestsNeeded(event.target.value)} placeholder="Enter tests needed" />
      </section>

      <section className="opd-section">
        <CkEditorField label="Advice" value={advice} onChange={setAdvice} placeholder="Advice for the patient" />
      </section>

      <div className="opd-form-actions">
        <button className="button button-primary" type="button" disabled={saveMutation.isPending || (editMode && query.isLoading)} onClick={submit}>{saveMutation.isPending ? 'Saving...' : editMode ? 'Update Prescription' : 'Create E-Prescription'}</button>
      </div>
    </div>
  );
};
